import { useRuntimeConfig, useSessionContext } from '#imports'
import type { Schemas } from '#shopware'

interface EightSelectViewEvent {
    type: 'view'
    view: {
        type: 'matchingProductClusters'
        matchingProductClusters: { id: string }
    }
    context: Array<{
        type: 'user'
        user: { id: string }
    }>
}

interface EightSelectInteractEvent {
    type: 'interact'
    interact: {
        action: 'click'
        type: 'product'
        product: { sku: string }
    }
    context: Array<{
        type: 'content' | 'user'
        content?: {
            matchingProductClusters: { id: string }
            type: 'matchingProductClusters'
        }
        user?: { id: string }
    }>
}

interface EightSelectOrderEvent {
    type: 'order'
    order: {
        id: string
        lines: Array<{
            sku: string
            quantity: number
            grossPrice: {
                amount: number
                currency: string
            }
        }>
    }
    context: Array<{
        type: 'user'
        user: { id: string }
    }>
}

type EightSelectEvent = EightSelectViewEvent | EightSelectInteractEvent | EightSelectOrderEvent

const USER_ID_STORAGE_KEY = '8select_user_id'

export function use8selectTracking() {
    const config = useRuntimeConfig()
    const { currency } = useSessionContext()

    function generateUUIDv4(): string {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
            const r = Math.random() * 16 | 0
            const v = c === 'x' ? r : (r & 0x3 | 0x8)
            return v.toString(16)
        })
    }

    function isValidUUIDv4(uuid: string): boolean {
        const uuidv4Regex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
        return uuidv4Regex.test(uuid)
    }

    function getUserId(): string {
        if (typeof window === 'undefined') {
            return 'server-side-fallback'
        }

        try {
            let userId = localStorage.getItem(USER_ID_STORAGE_KEY)

            if (!userId || !isValidUUIDv4(userId)) {
                userId = generateUUIDv4()
                localStorage.setItem(USER_ID_STORAGE_KEY, userId)
                console.log('[8select] Generated new user ID:', userId)
            }

            return userId
        } catch (err) {
            console.error('[8select] Failed to get/set user ID:', err)
            return generateUUIDv4()
        }
    }

    async function sendTrackingEvent(
        event: EightSelectEvent
    ): Promise<boolean> {
        const apiId = config.public.eightSelectApiId
        const validate = process.env.NODE_ENV === 'development'

        if (!apiId) {
            console.error('[8select] API ID not configured for tracking')
            return false
        }

        const endpoint = validate
            ? 'https://api.8select.io/analytics/v3/events/validate'
            : 'https://api.8select.io/analytics/v3/events'

        try {
            const response = await $fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-api-id': apiId
                },
                body: event
            })

            if (validate) {
                console.log('[8select] Tracking validation response:', response)
            }

            return true
        } catch (err) {
            console.error('[8select] Tracking error:', err)
            return false
        }
    }

    async function trackView(sourceProductId: string): Promise<void> {
        if (!sourceProductId) {
            console.warn('[8select] Cannot track view: missing source product ID')
            return
        }

        const userId = getUserId()

        const event: EightSelectViewEvent = {
            type: 'view',
            view: {
                type: 'matchingProductClusters',
                matchingProductClusters: { id: sourceProductId }
            },
            context: [
                {
                    type: 'user',
                    user: { id: userId }
                }
            ]
        }

        await sendTrackingEvent(event)

        console.log('[8select] Tracked view event:', {
            type: 'matchingProductClusters',
            sourceProductId,
            userId
        })
    }

    async function trackInteract(
        productSku: string,
        sourceProductId: string,
        action: 'click'
    ): Promise<void> {
        if (!productSku || !sourceProductId) {
            console.warn('[8select] Cannot track interact: missing SKU or source product ID')
            return
        }

        const userId = getUserId()

        const event: EightSelectInteractEvent = {
            type: 'interact',
            interact: {
                action,
                type: 'product',
                product: { sku: productSku }
            },
            context: [
                {
                    type: 'content',
                    content: {
                        matchingProductClusters: { id: sourceProductId },
                        type: 'matchingProductClusters'
                    }
                },
                {
                    type: 'user',
                    user: { id: userId }
                }
            ]
        }

        await sendTrackingEvent(event)

        console.log('[8select] Tracked interact event:', event)
    }

    async function trackOrder(order: Schemas['Order']): Promise<void> {
        if (!import.meta.client) {
            return
        }

        if (!order || !order.id || !order.lineItems || order.lineItems.length === 0) {
            console.warn('[8select] placeOrder event received but no order data')
            return
        }

        const lineItems = order.lineItems
            .filter((item) => item.type === 'product')
            .map(item => ({
                sku: item.payload?.productNumber ?? 'unknown',
                quantity: item.quantity,
                grossPrice: {
                    amount: item.unitPrice!,
                    currency: currency.value?.isoCode ?? 'EUR'
                }
            }))

        if (lineItems.length === 0) {
            console.warn('[8select] Order has no product line items to track')
            return
        }

        const userId = getUserId()

        const event: EightSelectOrderEvent = {
            type: 'order',
            order: {
                id: order.id,
                lines: lineItems
            },
            context: [
                {
                    type: 'user',
                    user: { id: userId }
                }
            ]
        }

        await sendTrackingEvent(event)

        console.log('[8select] Tracked order event:', event)
    }

    return {
        trackView,
        trackInteract,
        trackOrder,
        getUserId,
    }
}
